<template>
    <Loading :loading="loading" class="min-h-screen">
        <div class="min-h-screen w-full bg-[#f1f6fa] px-6 lg:px-20 lg:py-10 flex flex-col gap-6">
            <MarketsHeader v-if="!productGroupIsloading" :current-product-group="currentProductGroup" />

            <div class="flex flex-col lg:flex-row gap-4">
                <Sheet class="w-full lg:w-2/3">
                    <div class="xxl:w-4/6">
                        <MetricsCards :card-objects="cardObjects" :title="i18next.t('plain_overview')" />
                    </div>
                    <hr class="bg-gray-300 my-8" />
                    <CalendarView v-if="currentProductGroup && productIds.length > 0" :current-product-id="productIds[0]" />
                </Sheet>

                <ContentstreamsListV2
                    v-if="currentProduct.length"
                    :page-size="40"
                    class="w-full lg:w-1/3 h-fit shadow-md"
                    :show-dropdown="false"
                    :product-id="currentProduct[0].id"
                    :init-filter="['short', 'webinar', 'wmu', 'dsu', 'dmu']"
                ></ContentstreamsListV2>
            </div>

            <div class="flex flex-col lg:flex-row gap-4">
                <TradingviewChart v-if="currentProduct.length > 0" :product-id="currentProduct[0].id" layout="list" class="bg-white rounded-xl p-10 xl:px-4 h-fit shadow-md" />
                <CurrentPriceTargets v-if="currentProduct.length" class="w-full lg:w-1/3 h-fit shadow-md" :product-id="currentProduct[0].id" />
            </div>
            <Sheet v-if="currentProductGroup && productIds.length > 0" class="w-full">
                <MarketsSocials :current-product-group="currentProductGroup" :product-ids="productIds" />
            </Sheet>
        </div>
    </Loading>
</template>

<script setup>
import i18next from 'i18next';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { asset } from '../../../helpers.js';
import CalendarView from '../../../views/CalendarView.vue';
import Loading from '../../miscellaneous/Loading.vue';
import CurrentPriceTargets from '../../priceTargets/CurrentPriceTargets.vue';
import Sheet from '../../Sheet.vue';
import ContentstreamsListV2 from '../ContentstreamsListV2.vue';
import MarketsHeader from '../MarketsHeader.vue';
import MarketsSocials from '../MarketsSocials.vue';
import MetricsCards from '../portfolio/MetricsCards.vue';
import TradingviewChart from '../TradingviewChart.vue';

const store = useStore();
const currentProduct = ref({});
const loading = ref(true);
const route = useRoute();
const productGroupId = route.params.id;

const contentstreams = ref([]);
const currentProductGroup = ref([]);
const currentProductGroupName = ref('');
const currentProductGroupDescription = ref('');

const tabs = ref([]);
const productIds = ref([]);
const activeTradesAmountOfProduct = ref();
const closedTradesAmountOfProduct = ref();
const trades = ref([]);
const productGroupIsloading = ref(true);

const cardObjects = computed(() => [
    {
        icon: asset('/hkcm/images/tradesChartIcon.svg', 'public'),
        number: activeTradesAmountOfProduct.value,
        title: 'open_trades_title',
        description: 'open_trades_description',
        link: '/dashboard/trades'
    },
    {
        icon: asset('/hkcm/images/tradesChartClosedIcon.svg', 'public'),
        number: closedTradesAmountOfProduct.value,
        title: 'closed_trades_title',
        description: 'closed_trades_description',
        link: '/dashboard/trades'
    }
]);

/**
 * Get content streams by product id
 * @param ids of products in current Productgroup string
 */
const fetchAndProcessContentStreams = async (productIds) => {
    const get = await store.getters['contentstreams/all'];
    contentstreams.value = get?.dashboard?.data.filter((item) => productIds.includes(item.product_id)).sort((a, b) => new Date(b.published_at) - new Date(a.published_at));

    loading.value = false;
};

const contentstreamIsLoaded = computed(() => store.getters['contentstreams/all'].dashboard);
watch([contentstreamIsLoaded, productIds], ([isLoaded, ids]) => {
    if (isLoaded && ids.length) {
        fetchAndProcessContentStreams(ids);
    }
});

//
/**
 *  If one of the Quick Markets Accesses in the sidebar menu is clicked,
 *  refresh the main content of the page to display the selected market's data.
 * @returns {void}
 */
watch(
    () => route.params.id,
    async (newId, oldId) => {
        if (newId !== oldId && route.name.includes('markets-')) {
            loading.value = true;
            resetPageData();
            await fetchAndProcessProductGroups(newId);
            await fetchTrades();
            loading.value = false;
        }
    }
);

/**
 * Resets the page data to its initial state.
 * @returns {void}
 */
const resetPageData = () => {
    contentstreams.value = [];
    productIds.value = [];
    trades.value = [];
    activeTradesAmountOfProduct.value = null;
    closedTradesAmountOfProduct.value = null;
    currentProductGroup.value = [];
    currentProduct.value = [];
    tabs.value = [];
};

/**
 * Fetches product groups by ID and updates the current product group details.
 *
 * @param {string} id - The ID of the product group.
 * @returns {void}
 */
const fetchAndProcessProductGroups = async (id) => {
    productGroupIsloading.value = true;

    await store.dispatch('product_group/show', id).then(() => {
        currentProductGroup.value = store.getters['product_group/get'];
        currentProductGroupName.value = currentProductGroup?.value?.name;
        currentProductGroupDescription.value = currentProductGroup?.value?.description;
        currentProduct.value = currentProductGroup?.value?.products?.data;

        productIds.value = currentProduct.value.map((product) => product.id);

        productGroupIsloading.value = false;
    });
};

/**
 * Fetches trades, filters them by product and gets the amount of active and closed trades.
 * @returns {void}
 */
const fetchTrades = async () => {
    trades.value = store.getters['settings/isContentLoaded']('trades')
        ? store.getters['trades/get'].data?.data
        : (
              await store.dispatch('trades/index', {
                  params: {
                      sort: '',
                      subscribed_abos: true
                  }
              })
          ).data || [];

    filterTradesByProduct();
    fetchActiveAndClosedTradesAmount();
};

/**
 * Filters trades by product.
 * @returns {void}
 */
const filterTradesByProduct = () => {
    trades.value = trades.value.filter((trade) => trade.market_symbol.products.data.some((productgroup) => productgroup.id === productIds.value[0]));
};

/**
 * Gets the amount of active and closed trades.
 * @returns {void}
 */
const fetchActiveAndClosedTradesAmount = () => {
    activeTradesAmountOfProduct.value = trades.value.filter((trade) => !trade?.exit_price).length;
    closedTradesAmountOfProduct.value = trades.value.filter((trade) => trade?.exit_price).length;
};

onMounted(async () => {
    await fetchAndProcessProductGroups(productGroupId);
    await fetchTrades();
});
</script>

<style scoped>
::v-deep(.heading h2) {
    line-height: 1.4;
    margin-top: 0;
    margin-bottom: 1rem;
}
</style>
